// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { State } from "../../types/types";

interface NetworkLogsState {
  state: State;
  stateMessage: string | null;
  errorMessage: string | null;
  logs: NetworkLogs[] | null;
}

interface NetworkLogs {
  accessTimeSessionId: string;
  accessStartTime: string;
  accessStopTime: string;
  calledStationId: string;
  floorNo: number | null;
  description: string;
  error?: string;
}

const initialState: NetworkLogsState = {
  state: State.idle,
  stateMessage: "",
  errorMessage: "",
  logs: null,
};

export const fetchNetworkLogs = createAsyncThunk(
  "networkLogs/fetchNetworkLogs",
  async (args: { email: string; date: string }, thunkAPI) => {
    return new Promise<NetworkLogs[]>((resolve, reject) => {
      APIService.getInstance()
        .get(
          `${AppConfig.serviceUrls.getNetlogs}?email=${args.email}&date=${args.date}`
        )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  }
);

const networkLogsSlice = createSlice({
  name: "networkLogs",
  initialState,
  reducers: {
    clearNetworkLogs: (state) => {
      state.logs = null;
      state.state = State.idle;
      state.stateMessage = "";
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNetworkLogs.pending, (state) => {
        state.state = State.loading;
        state.stateMessage = "Fetching network logs...";
      })
      .addCase(fetchNetworkLogs.fulfilled, (state, action) => {
        state.logs = action.payload;
        state.state = State.success;
        state.stateMessage = "Successfully fetched network logs!";
      })
      .addCase(fetchNetworkLogs.rejected, (state, action) => {
        state.state = State.failed;
        state.stateMessage = "Failed to fetch network logs!";
        state.errorMessage = action.payload
          ? (action.payload as string)
          : "User details not found for this email";
        state.logs = null;
      });
  },
});

export const { clearNetworkLogs } = networkLogsSlice.actions;
export default networkLogsSlice.reducer;
